import React, { memo, useContext, useEffect, useState } from 'react';
import { useExperiment } from '@olx/react-laquesis';
import { fetchUnifiedPlatformAPI } from '@olxeu-a2b/delivery-utils/src/extras';
import { SiteConfig } from '@olxeu-a2b/delivery-utils/typings/config';
import { useI18n } from '../../../../i18n';
import TransactionContext from '../contexts/TransactionContext';
import S from './style/ActionAcceptance';
import Shared from './style/Shared';

interface ActionAcceptanceProps {
  onAccept: React.EventHandler<React.MouseEvent>;
  onReject: React.EventHandler<React.MouseEvent>;
  disabled?: boolean;
  resolvedUrl?: string;
  desktop?: boolean;
  baseDomains?: {
    desktop: SiteConfig;
    mweb: SiteConfig;
    delivery: SiteConfig;
  };
}

const ActionAcceptance: React.FC<ActionAcceptanceProps> = function ActionAcceptance({
  disabled,
  onAccept,
  onReject,
  resolvedUrl,
  desktop,
  baseDomains,
}) {
  const i18n = useI18n();
  const transaction = useContext(TransactionContext);
  const [isSalesOrderInUnifiedPlatform, setIsSalesOrderInUnifiedPlatform] = useState(false);

  /**
   * Check if the sales order exists in the unified platform.
   */
  const checkSalesOrderExistence = (): Promise<boolean> => {
    const salesOrderExistenceEndpoint = `order/v1/sales-order/${transaction?.id}/exists`;

    return fetchUnifiedPlatformAPI({
      endpoint: salesOrderExistenceEndpoint,
      contentType: 'text/html',
    }).then(res => res.status === 200);
  };

  useEffect(() => {
    checkSalesOrderExistence()
      .then(res => setIsSalesOrderInUnifiedPlatform(res))
      .catch(() => {
        setIsSalesOrderInUnifiedPlatform(false);
      });
  }, []);

  const isUnifiedSellerConfirmation =
    useExperiment('cou-1752') === 'b' && isSalesOrderInUnifiedPlatform;

  const unifiedSellerConfirmationURL = desktop
    ? `${baseDomains?.desktop?.url ?? ''}/delivery/seller-confirmation/${transaction?.id}/`
    : `${baseDomains?.mweb?.url ?? ''}/delivery/seller-confirmation/${transaction?.id}/`;

  const href = desktop
    ? `${resolvedUrl}/orders/confirmation/${transaction?.order.id}/${transaction?.ad.id}`
    : `/orders/confirmation/${transaction?.order.id}/${transaction?.ad.id}`;

  return (
    <Shared.OrderActions>
      <S.RejectButton
        data-cy="rejection-btn"
        data-testid="rejection-btn"
        data-order-id={transaction?.order.id}
        data-advert-id={transaction?.ad.id}
        disabled={disabled}
        onClick={onReject}
      >
        {i18n`dlv_orders_reject`}
      </S.RejectButton>
      <S.AcceptButton
        data-cy="acceptance-btn"
        data-testid="acceptance-btn"
        data-order-id={transaction?.order.id}
        data-advert-id={transaction?.ad.id}
        as="a"
        href={isUnifiedSellerConfirmation ? unifiedSellerConfirmationURL : href}
        disabled={disabled}
        onClick={onAccept}
      >
        {i18n`dlv_orders_accept`}
      </S.AcceptButton>
    </Shared.OrderActions>
  );
};

export default memo(ActionAcceptance);
